import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import '../../admin.css';
// After admin.css: the console's own rules win a tie with it (never with controls.css — trap 1).
import './platform.css';
import { PublicRules, adminLogout, adminMe } from '../../api/admin';
import { ChangePasswordModal, setKnownMinPassword } from '../../components/PasswordDialogs';
import { useToast } from '../../components/Toast';
import { NARROW, useMediaQuery } from '../../shared/media';
import { useSessionExpiry } from '../../shared/session';
import { RowMenu, SectionLabel, SideNav } from '../../components/fs';
import { CommandPalette, SearchGlyph, initialsOf, useShellShortcuts } from '../../components/CommandPalette';
import { shellCommands } from '../../shared/commandIndex';
import { PLATFORM_NAV, PlatformPage } from '../../shared/platformNav';
import { PLATFORM_ROLE_LABEL, PlatformRole } from '../../shared/types';

/**
 * The service owner's shell.
 *
 * The console outgrew one page: customers, the plan catalogue and the people
 * who run the service are three different jobs that happen to need the same
 * chrome. The tab row lives here so each of them carries the same one, and so
 * the platform-admin guard runs once rather than in every page.
 *
 * R1: the same white 52px bar as the account shell, with a dark
 * "Platform · {role}" pill, the ⌘K box and the avatar menu. There is no
 * "← Back to {account}": leaving support mode leaves the account (Ben,
 * 2026-09-24).
 *
 * R12 (§1a, screens 37–45): the console is rail + content, edge to edge —
 * the settings frame's `.st-page`, with the grouped sidebar (Customers ·
 * Growth · Operations · Audit, shared/platformNav.ts) in the rail and the
 * page beside it, never in the centred `.ad-body` column. The bar's nav is
 * the design's single "Console" link. Under 900 the ☰ drawer lists every
 * destination, grouped, and is the console's one menu: the rail is not drawn
 * at all, because its chip row repeated the drawer link for link (Ben,
 * 2026-09-27).
 */
export interface PlatformContext {
  /** the signed-in staff member's email, for "You" markers */
  me: string;
  role: PlatformRole | null;
  /**
   * The customer-facing rules the service runs on (Platform › Rules), for the
   * console's copy that names one — a reminder's lead time, the deletion
   * grace. Null from an older server; each reader keeps the code's number.
   */
  rules: PublicRules | null;
}

export const usePlatform = () => useOutletContext<PlatformContext>();

/** A row's own route, or one of the paths it also stays lit on (Accounts on a billing page). */
const litOn = (page: PlatformPage, pathname: string) => !!page.also?.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export default function PlatformLayout() {
  const [ctx, setCtx] = useState<PlatformContext | null>(null);
  /** The idle window, from the server; the console is thrown out the same way
   *  the customer shell is — see shared/session.ts. */
  const [sessionIdleMs, setSessionIdleMs] = useState(0);
  /** Your own password, changeable from wherever you are (rule 6: one modal). */
  const [changing, setChanging] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  useSessionExpiry(sessionIdleMs);
  // Under 900px every console page and the account links live in a menu (F2);
  // nothing of it exists on a desktop. Closes on route change, Escape, scrim.
  const narrow = useMediaQuery(NARROW);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); setAvatarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const onLogout = useCallback(async () => {
    await adminLogout().catch(() => undefined);
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    adminMe()
      .then((r) => {
        if (!r.isPlatformAdmin) { navigate('/admin', { replace: true }); return; }
        setCtx({ me: r.admin.email, role: r.platformRole, rules: r.rules ?? null });
        setSessionIdleMs(r.sessionIdleMs || 0);
        setKnownMinPassword(r.rules?.minPasswordLength);
      })
      .catch(() => navigate('/login', { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPalette = useCallback(() => { setMenuOpen(false); setAvatarOpen(false); setPaletteOpen(true); }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  useShellShortcuts({ onPalette: openPalette, onLogout });
  const openChangePassword = useCallback(() => { setMenuOpen(false); setAvatarOpen(false); setChanging(true); }, []);
  const commands = useMemo(() => (ctx ? shellCommands({
    me: { admin: { id: 0, email: ctx.me, name: null, role: 'superadmin' }, account: null, features: null, masquerading: false, isPlatformAdmin: true },
    pathname: location.pathname,
    onChangePassword: openChangePassword,
    onLogout,
  }) : []), [ctx, location.pathname, openChangePassword, onLogout]);

  const roleLabel = ctx?.role ? PLATFORM_ROLE_LABEL[ctx.role] : 'Staff';
  const avatarHead = <><b>{ctx?.me}</b><span>Form-Magic Platform · {roleLabel}</span></>;

  return (
    // `pl-console` is what M7's rules scope to: the console's forms carry
    // inline `min-width`s that only a phone needs released.
    <div className="ad-wrap pl-console">
      <header className="ad-top">
        <Link to="/" className="brand"><span className="ad-mark" aria-hidden="true" />Form-Magic</Link>
        <span className="ad-acct platform" title="The platform console">
          <span className="ad-acct-mark" aria-hidden="true">FM</span>
          <span className="ad-acct-name">Platform · {roleLabel}</span>
        </span>
        {!narrow && (
          <>
            {/* The design's one link: the pages themselves are the sidebar's. Lit on every /platform path. */}
            <nav className="ad-nav" aria-label="Platform">
              <NavLink to="/platform">Console</NavLink>
            </nav>
            <span className="spacer" />
            <button type="button" className="ad-search" onClick={openPalette} aria-label="Search pages and settings (Ctrl or ⌘ K)">
              <SearchGlyph />
              <span className="ad-search-text">Search pages, settings…</span>
              <kbd>⌘K</kbd>
            </button>
            <RowMenu
              open={avatarOpen} onToggle={setAvatarOpen} label={ctx?.me} head={avatarHead} className="ad-avatar-menu"
              renderTrigger={({ open, onClick, onKeyDown }) => (
                <button
                  type="button" className={`ad-avatar${open ? ' open' : ''}`} aria-label="Account menu"
                  aria-haspopup="menu" aria-expanded={open} onClick={onClick} onKeyDown={onKeyDown}
                >
                  {initialsOf(null, ctx?.me)}
                </button>
              )}
            >
              {ctx && <button type="button" onClick={openChangePassword}>Change password</button>}
              <button type="button" className="danger" onClick={() => void onLogout()}>Log out</button>
            </RowMenu>
          </>
        )}
        {narrow && (
          <>
            <span className="spacer" />
            <button type="button" className="ad-search-icon" aria-label="Search" onClick={openPalette}><SearchGlyph size={20} /></button>
            <button
              type="button" className="ad-menu-btn" aria-label="Menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              ☰
            </button>
          </>
        )}
      </header>
      {narrow && menuOpen && (
        <>
          <div className="fs-scrim" onClick={() => setMenuOpen(false)} />
          <div className="fs-drawer ad-menu" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="ad-menu-head">
              <span className="ad-avatar static" aria-hidden="true">{initialsOf(null, ctx?.me)}</span>
              <span className="who"><b>{ctx?.me}</b><span>Form-Magic Platform · {roleLabel}</span></span>
              <button type="button" className="fs-btn icon" title="Close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            <nav className="ad-menu-list">
              {PLATFORM_NAV.map((g) => (
                <Fragment key={g.label}>
                  <SectionLabel>{g.label}</SectionLabel>
                  {g.items.map((it) => (
                    <NavLink
                      key={it.to} to={it.to} end={it.end}
                      className={({ isActive }) => (isActive || litOn(it, location.pathname) ? 'active' : '')}
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </Fragment>
              ))}
              <div className="rule" />
              {ctx && <button type="button" onClick={openChangePassword}>Change password</button>}
              <button type="button" onClick={() => { setMenuOpen(false); void onLogout(); }}>Log out</button>
            </nav>
          </div>
        </>
      )}
      {/* Nothing renders until the guard has answered — a page that flashes
          before redirecting shows a non-admin the shape of the console, so
          the sidebar waits for it too. Under 900 there is no sidebar: the
          ☰ drawer above already lists every page. */}
      <div className="ad-outlet-full">
        <div className="st-page pl-frame">
          {!narrow && (
            <aside className="st-side pl-side">
              {ctx && <SideNav groups={[...PLATFORM_NAV]} aria-label="Console" />}
            </aside>
          )}
          <div className="st-main pl-main">
            {ctx ? <Outlet context={ctx} /> : <div className="ad-card">Loading…</div>}
          </div>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onClose={closePalette} commands={commands} />
      {changing && ctx && (
        <ChangePasswordModal email={ctx.me} onClose={() => setChanging(false)}
          onDone={() => { setChanging(false); toast('Your password has been changed.'); }} />
      )}
    </div>
  );
}
